import { UIEventHandler, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import Text from '@/components/view/atoms/text/Text'

interface Props extends ChildProps {
  radius?: boolean
  onScroll?: UIEventHandler<HTMLDivElement>
  noPagination?: boolean
  bottomFloat?: boolean
  selectedQuantity?: number
  height?: number
  bottomPadding?: boolean
  scrollShowing?: boolean
}

const Table = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      radius,
      noPagination,
      bottomFloat = false,
      onScroll = undefined,
      selectedQuantity = 0,
      height,
      bottomPadding,
      scrollShowing = false
    },
    ref
  ) => {
    const [parentHeight, setParentHeight] = useState<number | undefined>(undefined)
    const [isScrollableX, setIsScrollableX] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement)

    useEffect(() => {
      const checkScrollability = () => {
        if (containerRef.current) {
          const { scrollWidth, clientWidth } = containerRef.current
          setIsScrollableX(scrollWidth > clientWidth)
        }
      }

      const handleResize = () => {
        const el = containerRef.current
        if (el?.parentElement) {
          const parentHeight = el.parentElement.clientHeight
          const contentHeight = el.scrollHeight
          if (contentHeight < parentHeight) {
            setParentHeight(parentHeight)
          } else {
            setParentHeight(undefined)
          }
        }
        checkScrollability()
      }

      handleResize()
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    return (
      <div
        className={clsx('relative min-h-0 w-full flex-1 overflow-hidden', bottomPadding && 'pb-8')}
      >
        <div
          className={clsx(
            'relative h-full min-h-full w-full',
            radius && 'rounded-t-lg',
            !bottomFloat && noPagination && 'rounded-b-lg',
            (isScrollableX || onScroll !== undefined) && 'overflow-y-hidden',
            (isScrollableX || onScroll !== undefined) && !scrollShowing && 'scrollbar-hidden-x'
          )}
          ref={containerRef}
          onScroll={onScroll}
          style={{ minHeight: parentHeight }}
        >
          {selectedQuantity > 0 && (
            <div className='pointer-events-none'>
              <TableHeadItem float covering type='low' height={height}>
                <Text weight='bold'>{selectedQuantity}個を選択済み</Text>
              </TableHeadItem>
            </div>
          )}
          <div className='inline-block min-w-full align-top'>
            <table className='w-max min-w-full table-fixed whitespace-nowrap'>{children}</table>
          </div>
        </div>
      </div>
    )
  }
)

export default Table
